import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Send, ExternalLink, Loader2, ArrowLeft, Save, Copy, Sparkles, AlertTriangle, Check, Circle, Image as ImageIcon, Video, FileText, Figma, BookOpen, Music, Lock, Beaker, Download, X, Mail, ChevronDown } from "lucide-react";
import { fileUrl, normalizePiece } from "@/lib/portfolio";
import AtsCompareModal from "@/components/AtsCompareModal";
import Collapsible from "@/components/Collapsible";

const PIECE_ICONS = {
  image: ImageIcon, video: Video, pdf: FileText, url: ExternalLink,
  figma: Figma, case_study: BookOpen, audio: Music, on_request: Lock,
};

export default function ApplicationReview() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [atsOpen, setAtsOpen] = useState(false);
  // Modal that explains what "Send" actually does (it's a tracker - user still
  // submits via the company's link) and forces the user to acknowledge they're
  // responsible for the accuracy of AI-generated content.
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  // Popover state - only one piece's PDF picker can be open at a time.
  // Value is the kind currently showing the For-Humans / For-AI menu (or null).
  const [pdfMenu, setPdfMenu] = useState(null);

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(`${label} copied`);
    } catch {
      toast.error("Couldn't copy - try selecting the text manually.");
    }
  };

  const downloadPdf = async (kind, mode) => {
    setPdfMenu(null);
    const labelMap = { resume: "resume", cover_letter: "cover letter", portfolio: "portfolio" };
    const id = toast.loading(`Generating ${labelMap[kind]} PDF…`);
    try {
      const res = await api.get(`/applications/${jobId}/pdf/${kind}/${mode}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `Sisyphean-${kind}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded", { id });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed", { id });
    }
  };

  // Close any open PDF picker on outside click / escape.
  useEffect(() => {
    if (!pdfMenu) return undefined;
    const onClick = (e) => {
      if (!e.target.closest('[data-testid^="pdf-picker-"]') && !e.target.closest('[data-testid^="download-"]')) {
        setPdfMenu(null);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setPdfMenu(null); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, [pdfMenu]);

  const load = async () => {
    const res = await api.get(`/applications/${jobId}`);
    setData(res.data);
    setEditing({
      resume: res.data.user_job?.materials?.resume || "",
      cover_letter: res.data.user_job?.materials?.cover_letter || "",
      portfolio_intro: res.data.user_job?.materials?.portfolio_intro || "",
    });
  };

  useEffect(() => { load(); }, [jobId]);

  // Poll if materials are still generating OR if the role summary hasn't landed yet
  useEffect(() => {
    if (!data) return;
    const matsGenerating = data.user_job?.materials_status === "generating";
    const summaryMissing = !data.job?.summary;
    if (pollCount < 30 && (matsGenerating || summaryMissing)) {
      const t = setTimeout(() => { load(); setPollCount(pollCount + 1); }, 3000);
      return () => clearTimeout(t);
    }
  }, [data, pollCount]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/applications/${jobId}/materials`, editing);
      toast.success("Materials saved");
      await load();
    } catch (err) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const send = async () => {
    if (!user?.has_active_access) {
      toast.error("Your free trial has ended - upgrade to send applications.");
      navigate("/subscription");
      return;
    }
    setSendConfirmOpen(false);
    try {
      await api.post(`/applications/${jobId}/send`);
      toast.success("Marked as sent. Now head to the posting and submit.");
      await load();
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error("Your free trial has ended - upgrade to send applications.");
        navigate("/subscription");
      } else {
        toast.error(err?.response?.data?.detail || "Send failed");
      }
    }
  };

  const optimizeForAI = async () => {
    setOptimizing(true);
    try {
      const res = await api.post(`/applications/${jobId}/optimize-resume`);
      setEditing((prev) => ({ ...prev, resume: res.data.resume }));
      const inferred = res.data.inferred_metrics || [];
      if (inferred.length) {
        toast.warning(`Resume optimized. ${inferred.length} metric${inferred.length === 1 ? "" : "s"} inferred - please verify before sending.`);
      } else {
        toast.success("Resume optimized for ATS / AI screeners.");
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Optimization failed");
    } finally { setOptimizing(false); }
  };

  if (!data) return <Layout><div className="p-12 text-center text-muted-foreground text-xs uppercase tracking-[0.3em]">LOADING<span className="blink-caret"/></div></Layout>;

  const { job, user_job, selected_pieces } = data;
  const generating = user_job?.materials_status !== "ready";
  const sent = user_job?.application_status === "sent";
  const isTestFixture = job?.source === "test_fixture" && user?.is_admin;

  return (
    <Layout>
      <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-6 sm:py-8">
        <button onClick={() => navigate("/dashboard")} className="btn-ghost inline-flex items-center gap-2 mb-6" data-testid="back-to-feed">
          <ArrowLeft className="w-3 h-3" /> Back to feed
        </button>

        {/* Header panel */}
        <div className="panel p-5 sm:p-8 mb-px relative">
          <div className="flex items-start justify-between flex-wrap gap-3 sm:gap-4">
            <div className="min-w-0 flex-1">
              <div className="terminal-label text-primary mb-2">[ APPLICATION / {job.id.substring(0, 8).toUpperCase()} ]</div>
              <h1 className="font-display text-xl sm:text-2xl md:text-4xl uppercase font-bold tracking-tight break-words leading-tight">{job.title}</h1>
              <div className="text-xs sm:text-sm text-muted-foreground mt-2 break-words">{job.company} · {job.location}</div>
              {job.salary && <div className="text-xs text-accent-green font-mono mt-1 break-words">{job.salary}</div>}
            </div>
            <div className="flex items-center gap-3 sm:gap-3 flex-wrap w-full sm:w-auto">
              {job.url && (
                <a
                  data-testid="view-original-posting"
                  href={job.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center gap-2 text-[10px]"
                  title="Open the original job posting in a new tab"
                >
                  <ExternalLink className="w-3 h-3" /> Original posting
                </a>
              )}
              {user_job?.materials_status === "ready" && (
                <div className="flex items-center gap-5 sm:gap-4 ml-auto sm:ml-0" data-testid="score-row">
                  <Score label="Match" value={user_job.match_score} color="#00FF41" />
                  <Score label="Enjoy" value={user_job.preference_score} color="#FFB000" />
                </div>
              )}
            </div>
          </div>
          {user_job?.materials_status === "ready" && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-2">
              <Collapsible
                label="Why it matches"
                accent="green"
                summary={user_job.match_reasoning}
                testId="why-it-matches"
              >
                <p className="text-sm text-foreground/85 leading-relaxed">{user_job.match_reasoning}</p>
              </Collapsible>
              <Collapsible
                label="Why you'd enjoy it"
                accent="amber"
                summary={user_job.preference_reasoning}
                testId="why-youd-enjoy"
              >
                <p className="text-sm text-foreground/85 leading-relaxed">{user_job.preference_reasoning}</p>
              </Collapsible>
            </div>
          )}
        </div>

        <RoleOverview job={job} profileSummary={data.user_profile_summary} />

        {generating && (
          <div className="panel p-6 sm:p-12 text-center">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-primary mb-4" />
            <div className="terminal-label text-primary">[ GENERATING.MATERIALS ]</div>
            <p className="text-sm text-muted-foreground mt-3">Claude is tailoring your resume, cover letter, and portfolio. ~10-30 seconds.</p>
          </div>
        )}

        {!generating && (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border border border-border">
            {/* Resume */}
            <Section title="Tailored resume" testId="materials-resume">
              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  {user_job?.materials?.ats_optimized && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-primary border border-primary px-2 py-1" data-testid="ats-optimized-badge">
                      <Sparkles className="w-3 h-3" /> ATS optimized
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    data-testid="copy-resume-btn"
                    onClick={() => copyText(editing.resume, "Resume text")}
                    className="btn-secondary inline-flex items-center gap-2 text-[10px]"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                  <div className="relative inline-block">
                    <button
                      data-testid="download-resume-pdf"
                      onClick={() => setPdfMenu(pdfMenu === "resume" ? null : "resume")}
                      className="btn-secondary inline-flex items-center gap-2 text-[10px]"
                    >
                      <Download className="w-3 h-3" /> PDF
                    </button>
                    {pdfMenu === "resume" && (
                      <PdfPicker kind="resume" onPick={downloadPdf} />
                    )}
                  </div>
                  <button
                    data-testid="optimize-for-ai-btn"
                    onClick={optimizeForAI}
                    disabled={optimizing}
                    className="btn-secondary inline-flex items-center gap-2 text-[10px]"
                    title="Rewrite resume to mirror the job posting and pass AI screeners"
                  >
                    <Sparkles className={`w-3 h-3 ${optimizing ? "animate-pulse" : ""}`} />
                    {optimizing ? "Optimizing..." : "Optimize for AI"}
                  </button>
                </div>
              </div>
              {user_job?.materials?.inferred_metrics?.length > 0 && (
                <div className="mb-3 panel p-3 border-accent-amber" data-testid="inferred-metrics-warning">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-3 h-3 text-accent-amber mt-1 flex-shrink-0" />
                    <div className="text-xs">
                      <div className="terminal-label text-accent-amber mb-1">Verify before sending</div>
                      <ul className="text-foreground/80 space-y-1">
                        {user_job.materials.inferred_metrics.map((m, i) => (
                          <li key={i} className="text-xs">• {m}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              <textarea
                data-testid="resume-editor"
                className="input-base min-h-[420px] font-mono text-xs"
                value={editing.resume}
                onChange={(e) => setEditing({ ...editing, resume: e.target.value })}
              />
            </Section>
            {/* Cover letter */}
            <Section
              title="Cover letter"
              testId="materials-cover"
              collapsible
              defaultOpen={false}
              summary={`${(editing.cover_letter || "").split(/\s+/).filter(Boolean).length} words. Tailored to this posting in your voice. Click to view or edit.`}
            >
              <div className="flex items-center justify-end mb-3 gap-2 flex-wrap">
                <button
                  data-testid="copy-cover-btn"
                  onClick={() => copyText(editing.cover_letter, "Cover letter text")}
                  className="btn-secondary inline-flex items-center gap-2 text-[10px]"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
                <div className="relative inline-block">
                  <button
                    data-testid="download-cover-pdf"
                    onClick={() => setPdfMenu(pdfMenu === "cover_letter" ? null : "cover_letter")}
                    className="btn-secondary inline-flex items-center gap-2 text-[10px]"
                  >
                    <Download className="w-3 h-3" /> PDF
                  </button>
                  {pdfMenu === "cover_letter" && <PdfPicker kind="cover_letter" onPick={downloadPdf} />}
                </div>
              </div>
              <textarea
                data-testid="cover-letter-editor"
                className="input-base min-h-[420px] font-mono text-xs"
                value={editing.cover_letter}
                onChange={(e) => setEditing({ ...editing, cover_letter: e.target.value })}
              />
            </Section>
            {/* Portfolio */}
            <div className="bg-background p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="terminal-label text-primary">[ PORTFOLIO.PAGE ]</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    data-testid="copy-portfolio-intro-btn"
                    onClick={() => {
                      const pieces = (data.selected_pieces || []).map((p, i) => {
                        const desc = user_job?.materials?.portfolio_piece_descriptions?.[p.id] || p.description || "";
                        return `${i + 1}. ${p.title} (${(p.work_type || "").replace(/_/g, " ")})\n   ${desc}`;
                      }).join("\n\n");
                      const text = `${editing.portfolio_intro}\n\nSelected work:\n\n${pieces}`;
                      copyText(text, "Portfolio text");
                    }}
                    className="btn-secondary inline-flex items-center gap-2 text-[10px]"
                  >
                    <Copy className="w-3 h-3" /> Copy text
                  </button>
                  <div className="relative inline-block">
                    <button
                      data-testid="download-portfolio-pdf"
                      onClick={() => setPdfMenu(pdfMenu === "portfolio" ? null : "portfolio")}
                      className="btn-secondary inline-flex items-center gap-2 text-[10px]"
                    >
                      <Download className="w-3 h-3" /> PDF
                    </button>
                    {pdfMenu === "portfolio" && <PdfPicker kind="portfolio" onPick={downloadPdf} />}
                  </div>
                  {user_job?.share_slug && (
                    <>
                      <Link to={`/p/${user_job.share_slug}`} target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center gap-2 text-[10px]" data-testid="open-portfolio-link">
                        <ExternalLink className="w-3 h-3" /> Open page
                      </Link>
                      <button
                        data-testid="copy-portfolio-link"
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/p/${user_job.share_slug}`); toast.success("Link copied"); }}
                        className="btn-secondary inline-flex items-center gap-2 text-[10px]"
                      >
                        <Copy className="w-3 h-3" /> Copy link
                      </button>
                    </>
                  )}
                </div>
              </div>
              <textarea
                data-testid="portfolio-intro-editor"
                className="input-base min-h-[100px] font-mono text-xs mb-4"
                value={editing.portfolio_intro}
                onChange={(e) => setEditing({ ...editing, portfolio_intro: e.target.value })}
                placeholder="Portfolio intro..."
              />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {selected_pieces.map((rawP) => {
                  const p = normalizePiece(rawP);
                  const Icon = PIECE_ICONS[p.piece_type] || ImageIcon;
                  // Find a thumbnail: prefer first image_path, then case_study image, else null
                  const thumb = p.image_paths?.[0] || p.image_path
                    || p.case_study?.problem?.image_paths?.[0]
                    || p.case_study?.process?.image_paths?.[0]
                    || p.case_study?.outcome?.image_paths?.[0];
                  return (
                    <div key={p.id} className="panel p-3" data-testid={`review-piece-${p.id}`}>
                      <div className="aspect-video bg-surface mb-2 flex items-center justify-center overflow-hidden">
                        {thumb ? (
                          <img src={fileUrl(thumb)} alt={p.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                        ) : (
                          <Icon className="w-6 h-6 text-muted-foreground" strokeWidth={1.25} />
                        )}
                      </div>
                      <div className="text-xs font-mono font-medium truncate">{p.title}</div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground truncate">{p.work_type}</div>
                        {p.visibility === "on_request" && (
                          <span className="text-[9px] tracking-[0.18em] uppercase text-primary font-mono">on req.</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {selected_pieces.length === 0 && (
                  <div className="col-span-full text-xs text-muted-foreground p-4 border border-dashed border-border">
                    No pieces selected. Add portfolio pieces in your profile.
                  </div>
                )}
              </div>
            </div>
          </div>
          </>
        )}

        <div className="panel p-6 mt-px flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-muted-foreground max-w-xl leading-relaxed">
            {sent ? (
              <span className="text-accent-green uppercase tracking-[0.2em]">✓ MARKED SENT · {new Date(user_job.sent_at).toLocaleString()}</span>
            ) : (
              <>
                <span className="block text-foreground/80">
                  <span className="font-mono uppercase text-[10px] tracking-[0.2em] text-primary mr-2">Heads up</span>
                  Clicking "Mark as sent" only updates your tracker - it doesn&apos;t submit your application. Head to the original posting to actually apply.
                </span>
              </>
            )}
          </div>
          <div className="flex gap-3">
            {!generating && !sent && (
              <button onClick={save} disabled={saving} className="btn-secondary inline-flex items-center gap-2" data-testid="save-materials">
                <Save className="w-3 h-3" /> {saving ? "Saving..." : "Save"}
              </button>
            )}
            {!sent && isTestFixture && (
              <div className="relative" data-testid="test-send-cluster">
                <button
                  onClick={() => setSendConfirmOpen(true)}
                  disabled={generating}
                  data-testid="test-send-application"
                  className="btn-primary inline-flex items-center gap-2"
                  title="Test mode - choose ATS A/B or send to kanban"
                >
                  <Send className="w-3 h-3" /> Send
                </button>
              </div>
            )}
            {!sent && !isTestFixture && (
              <button onClick={() => setSendConfirmOpen(true)} disabled={generating} data-testid="send-application" className="btn-primary inline-flex items-center gap-2">
                <Send className="w-3 h-3" /> Send
              </button>
            )}
          </div>
        </div>
      </div>
      {isTestFixture && (
        <AtsCompareModal jobId={jobId} open={atsOpen} onClose={() => setAtsOpen(false)} />
      )}
      {sendConfirmOpen && (
        <SendConfirmModal
          job={job}
          postingUrl={job.url}
          onConfirm={send}
          onClose={() => setSendConfirmOpen(false)}
          userProfile={data?.user_profile_summary || { name: user?.email?.split("@")[0] }}
          isTestFixture={isTestFixture}
          onRunAts={() => setAtsOpen(true)}
        />
      )}
    </Layout>
  );
}

function Score({ label, value, color }) {
  return (
    <div className="text-right leading-none">
      <div className="terminal-label mb-1" style={{ color }}>{label}</div>
      <div className="font-display text-3xl sm:text-2xl tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function Section({ title, testId, children, collapsible = false, summary = "", defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!collapsible) {
    return (
      <div className="bg-background p-6" data-testid={testId}>
        <div className="terminal-label text-primary mb-3">[ {title.toUpperCase()} ]</div>
        {children}
      </div>
    );
  }
  return (
    <div className="bg-background" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId ? `${testId}-toggle` : undefined}
        aria-expanded={open}
        className="w-full flex items-center gap-3 p-6 text-left hover:bg-surface-hover transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="terminal-label text-primary">[ {title.toUpperCase()} ]</div>
          {summary && !open && <div className="text-xs text-muted-foreground mt-1.5 line-clamp-2 leading-snug">{summary}</div>}
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2} />
      </button>
      {open && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
}

function RoleOverview({ job, profileSummary }) {
  const summary = job?.summary;
  const [compare, setCompare] = useState(false);
  const [open, setOpen] = useState(false);

  return (
    <div className="panel mt-px" data-testid="role-overview">
      <div className="border-b border-border p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="terminal-label text-primary mb-1">[ ROLE.OVERVIEW ]</div>
            <div className="text-xs text-muted-foreground">A digest of the posting - what they want and what you&apos;d do.</div>
          </div>
          <div className="flex items-center gap-3">
            {summary && profileSummary && open && (
              <button
                data-testid="role-compare-toggle"
                onClick={() => setCompare((v) => !v)}
                className={`btn-secondary inline-flex items-center gap-2 text-[10px] ${compare ? "border-primary text-primary" : ""}`}
                title="Highlight must-haves you already cover in your profile"
              >
                <Sparkles className={`w-3 h-3 ${compare ? "text-primary" : ""}`} />
                {compare ? "Comparing" : "Compare to my profile"}
              </button>
            )}
            {summary && (
              <button
                data-testid="role-overview-toggle"
                onClick={() => setOpen((v) => !v)}
                className="btn-secondary inline-flex items-center gap-2 text-[10px]"
              >
                {open ? "Hide details" : "Show details"}
                <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
            )}
            {!summary && (
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono">
                <Loader2 className="w-3 h-3 animate-spin" /> Reading the posting…
              </div>
            )}
          </div>
        </div>
        {summary?.headline && (
          <p className="mt-4 serif-mod text-xl leading-snug max-w-3xl tracking-tight" style={{ fontWeight: 500 }} data-testid="role-overview-headline">
            {summary.headline}
          </p>
        )}
        {/* When collapsed: surface the first 2 must-haves as a teaser. */}
        {summary && !open && (summary.must_haves || []).length > 0 && (
          <div className="mt-4 text-xs text-muted-foreground" data-testid="role-overview-teaser">
            <span className="text-foreground/70">Top must-haves: </span>
            {(summary.must_haves || []).slice(0, 2).map((m, i) => (
              <span key={i}>
                {i > 0 && <span className="mx-1.5 text-foreground/30">·</span>}
                <span className="text-foreground/85">{m}</span>
              </span>
            ))}
            {(summary.must_haves || []).length > 2 && (
              <span className="ml-1.5 text-foreground/50">+ {(summary.must_haves || []).length - 2} more</span>
            )}
          </div>
        )}
      </div>

      {summary && open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border">
          <Block testId="role-responsibilities" label="What you'd do" items={summary.responsibilities} />
          <Block
            testId="role-must-haves"
            label="Must-haves"
            labelColor="text-primary"
            items={summary.must_haves}
            compare={compare}
            profileSummary={profileSummary}
          />
          <Block testId="role-nice-to-haves" label="Nice to have" items={summary.nice_to_haves} />
          <div className="bg-background p-5" data-testid="role-ideal-candidate">
            <div className="terminal-label mb-3">Who they want</div>
            <p className="text-sm text-foreground/80 leading-relaxed">
              {summary.ideal_candidate || <span className="text-muted-foreground">Not specified in the posting.</span>}
            </p>
            {compare && profileSummary && (
              <div className="mt-4 pt-4 border-t border-border" data-testid="role-compare-legend">
                <div className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground font-mono mb-2">Legend</div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="inline-flex items-center gap-2"><Check className="w-3 h-3 text-accent-green" /> covered</span>
                  <span className="inline-flex items-center gap-2"><Circle className="w-3 h-3 text-accent-amber" strokeWidth={2.5} /> partial</span>
                  <span className="inline-flex items-center gap-2"><AlertTriangle className="w-3 h-3 text-primary" /> gap</span>
                </div>
              </div>
            )}
            {job?.url && (
              <a
                href={job.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="role-overview-original-link"
                className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono text-muted-foreground hover:text-primary"
              >
                <ExternalLink className="w-3 h-3" /> Read the original posting
              </a>
            )}
          </div>
        </div>
      )}

      {!summary && (
        <div className="p-6 text-xs text-muted-foreground">
          Sisyphean is summarizing the role. This usually takes 5-15 seconds.
        </div>
      )}
    </div>
  );
}

// Best-effort email extraction from a free-text job posting. Returns the first
// likely contact email (preferring jobs@/careers@/hiring@ if present), or null.
function _extractEmail(text) {
  if (!text) return null;
  const matches = [...String(text).matchAll(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g)].map((m) => m[0]);
  if (matches.length === 0) return null;
  // Score: prefer obvious hiring inboxes.
  const score = (e) => {
    const local = e.split("@")[0].toLowerCase();
    if (["jobs", "careers", "hiring", "hr", "apply", "recruit", "recruiting", "talent"].some((p) => local.includes(p))) return 2;
    if (local.includes("hello") || local.includes("info") || local.includes("contact")) return 1;
    return 0;
  };
  return matches.sort((a, b) => score(b) - score(a))[0];
}

// Modal that fires before a user marks an application as sent.
// Two purposes:
//   1. Spell out that "Send" is a tracker - not an auto-submit. Provides
//      a direct CTA to the original posting + (when possible) an email-compose
//      button addressed to the hiring inbox extracted from the listing.
//   2. Make the user acknowledge they're responsible for verifying the accuracy
//      of everything Sisyphean generated (resume, cover letter, portfolio copy).
function SendConfirmModal({ job, postingUrl, onConfirm, onClose, userProfile, isTestFixture = false, onRunAts }) {
  const [ack, setAck] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const contactEmail = _extractEmail(job?.description) || _extractEmail(job?.url);

  if (isTestFixture) {
    return (
      <div
        data-testid="test-send-modal"
        className="fixed inset-0 z-[130] flex items-center justify-center px-4 bg-background/85 backdrop-blur-sm"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="panel-glass corner-marks relative w-full max-w-lg p-6 md:p-7">
          <button onClick={onClose} aria-label="Close" data-testid="test-send-close" className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
          <div className="terminal-label text-primary mb-3">[ TEST.MODE / WHAT.NEXT ]</div>
          <h3 className="font-display text-xl uppercase font-bold tracking-tight mb-3">Pick a test action</h3>
          <p className="text-sm text-foreground/75 leading-relaxed mb-6">
            This is a generated fixture. Choose how you want to use it.
          </p>
          <div className="grid grid-cols-1 gap-3">
            <button
              data-testid="test-action-ats"
              onClick={() => { onClose(); onRunAts?.(); }}
              className="brut-tile p-4 text-left hover:border-primary transition-colors group"
              style={{ borderTopWidth: 3, borderTopColor: "hsl(var(--accent-amber))" }}
            >
              <div className="flex items-start gap-3">
                <Beaker className="w-4 h-4 mt-0.5 text-accent-amber flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm uppercase font-semibold tracking-tight">Run ATS A/B test</div>
                  <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
                    Score the raw persona vs the Sisyphean-tailored materials against a synthetic ATS engine. Doesn&apos;t mark the application as sent.
                  </div>
                </div>
              </div>
            </button>
            <button
              data-testid="test-action-kanban"
              onClick={onConfirm}
              className="brut-tile p-4 text-left hover:border-primary transition-colors group"
              style={{ borderTopWidth: 3, borderTopColor: "hsl(var(--accent-green))" }}
            >
              <div className="flex items-start gap-3">
                <Send className="w-4 h-4 mt-0.5 text-accent-green flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm uppercase font-semibold tracking-tight">Send to test kanban</div>
                  <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
                    Marks this fixture as sent in your tracker. Use it to manually walk the card through Sent → Interviewing → Offer / Closed and verify the kanban flow.
                  </div>
                </div>
              </div>
            </button>
          </div>
          <div className="flex justify-end mt-5">
            <button
              data-testid="test-send-cancel"
              onClick={onClose}
              className="btn-ghost text-[11px]"
            >Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  const mailtoHref = contactEmail
    ? `mailto:${contactEmail}?subject=${encodeURIComponent(`Application - ${job?.title || "role"}${job?.company ? ` at ${job.company}` : ""}`)}&body=${encodeURIComponent(`Hi there,\n\nI'd like to apply for the ${job?.title || "role"}${job?.company ? ` at ${job.company}` : ""} position.\n\n[Paste your cover letter here - you can copy it from Sisyphean with one click.]\n\nMy portfolio for this role: ${typeof window !== "undefined" ? window.location.origin : ""}/p/...\n\nThanks,\n${userProfile?.name || ""}`)}`
    : null;

  return (
    <div
      data-testid="send-confirm-modal"
      className="fixed inset-0 z-[130] flex items-center justify-center px-4 bg-background/85 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="panel-glass corner-marks relative w-full max-w-lg p-6 md:p-7 max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} aria-label="Close" data-testid="send-confirm-close" className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>

        <div className="terminal-label text-primary mb-3">[ BEFORE.YOU.SEND ]</div>
        <h3 className="font-display text-xl uppercase font-bold tracking-tight mb-4">Two quick things</h3>

        <div className="space-y-4 text-sm text-foreground/85 leading-relaxed">
          <div className="flex items-start gap-3">
            <span className="text-primary font-mono text-xs mt-1 flex-shrink-0">01</span>
            <p>
              <span className="font-medium text-foreground">Sisyphean doesn&apos;t submit for you.</span>{" "}
              Clicking &quot;Send&quot; just moves this into your tracker. To actually apply, head to the company&apos;s site (or email below) and paste in what we generated.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-primary font-mono text-xs mt-1 flex-shrink-0">02</span>
            <p>
              <span className="font-medium text-foreground">Double-check what Sisyphean wrote.</span>{" "}
              AI gets things wrong. Dates, titles, numbers, and skills can be inferred or paraphrased.{" "}
              <span className="text-foreground/80">You&apos;re the one signing off - read every word before you click submit on the company&apos;s site.</span>
            </p>
          </div>
        </div>

        {/* Action shortcuts */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {postingUrl ? (
            <a
              data-testid="send-confirm-posting-link"
              href={postingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary inline-flex items-center justify-center gap-2 text-[11px] no-underline"
            >
              <ExternalLink className="w-3 h-3" /> Open job posting
            </a>
          ) : (
            <div className="px-3 py-2 text-[10px] text-muted-foreground/60 font-mono uppercase tracking-[0.18em] border border-dashed border-foreground/15 text-center">
              No posting URL
            </div>
          )}
          {contactEmail ? (
            <a
              data-testid="send-confirm-mailto"
              href={mailtoHref}
              className="btn-secondary inline-flex items-center justify-center gap-2 text-[11px] no-underline"
              title={`Compose email to ${contactEmail}`}
            >
              <Mail className="w-3 h-3" /> Email {contactEmail.length > 28 ? contactEmail.split("@")[0] + "@…" : contactEmail}
            </a>
          ) : (
            <div
              data-testid="send-confirm-no-email"
              className="px-3 py-2 text-[10px] text-muted-foreground/60 font-mono uppercase tracking-[0.18em] border border-dashed border-foreground/15 text-center"
              title="No email address found in this listing - apply through the posting"
            >
              No email in listing
            </div>
          )}
        </div>

        <label className="flex items-start gap-3 mt-6 cursor-pointer" data-testid="se
nd-confirm-ack-label">
          <input
            type="checkbox"
            data-testid="send-confirm-ack"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            className="mt-1 accent-primary w-4 h-4 flex-shrink-0"
          />
          <span className="text-xs text-foreground/85 leading-relaxed">
            I&apos;ve reviewed the resume, cover letter, and portfolio. I confirm the information is accurate, and I understand I still need to submit my application through {job?.company || "the company"}&apos;s site.
          </span>
        </label>

        <div className="flex justify-end gap-3 mt-6">
          <button
            data-testid="send-confirm-cancel"
            onClick={onClose}
            className="btn-ghost text-[11px]"
          >
            Cancel
          </button>
          <button
            data-testid="send-confirm-submit"
            onClick={onConfirm}
            disabled={!ack}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Send className="w-3 h-3" /> Send
          </button>
        </div>
      </div>
    </div>
  );
}

// Small dropdown that appears next to the PDF button asking who the file is for.
// Positioned absolutely beneath the trigger button.
function PdfPicker({ kind, onPick }) {
  return (
    <div
      data-testid={`pdf-picker-${kind}`}
      className="absolute top-full right-0 mt-1 z-30 w-56 panel-glass border border-foreground/30 shadow-lg p-1"
    >
      <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono border-b border-foreground/15">
        Who is this for?
      </div>
      <button
        data-testid={`pdf-picker-${kind}-humans`}
        onClick={() => onPick(kind, "humans")}
        className="block w-full text-left px-3 py-2.5 hover:bg-foreground/10 transition-colors"
      >
        <div className="text-xs font-medium">For a human</div>
        <div className="text-[10px] text-muted-foreground mt-0.5">Designed PDF · email or attach</div>
      </button>
      <button
        data-testid={`pdf-picker-${kind}-ai`}
        onClick={() => onPick(kind, "ai")}
        className="block w-full text-left px-3 py-2.5 hover:bg-foreground/10 transition-colors border-t border-foreground/10"
      >
        <div className="text-xs font-medium">For an AI / ATS</div>
        <div className="text-[10px] text-muted-foreground mt-0.5">Plain, single-column, no decoration</div>
      </button>
    </div>
  );
}
const COMPARE_STOPWORDS = new Set([
  "a","an","the","and","or","of","in","on","at","to","for","with","by","from","as","is","are","be","been",
  "have","has","had","you","your","our","we","they","their",
  "experience","experienced","ability","able","strong","solid","proven","track","record",
  "year","years","plus","minimum","required","requirement","required.","preferred","ideally",
  "skill","skills","knowledge","understanding","working","work","comfortable","familiar",
  "high","level","good","great","excellent","portfolio","demonstrating","demonstrated",
]);

function compareItem(text, profileSummary) {
  if (!profileSummary) return null;
  const blob = (profileSummary.blob || "").toLowerCase();
  const skills = (profileSummary.skills || []).map((s) => s.toLowerCase());
  const tokens = String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9+#./ -]/g, " ")
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 3 && !COMPARE_STOPWORDS.has(t));
  if (tokens.length === 0) return "partial";
  let hits = 0;
  for (const t of tokens) {
    const inBlob = blob.includes(t);
    const inSkills = skills.some((s) => s.includes(t) || t.includes(s));
    if (inBlob || inSkills) hits += 1;
  }
  const ratio = hits / tokens.length;
  if (ratio >= 0.6) return "match";
  if (ratio >= 0.3) return "partial";
  return "gap";
}

function Block({ label, items, testId, labelColor, compare, profileSummary }) {
  const arr = Array.isArray(items) ? items : [];
  return (
    <div className="bg-background p-5" data-testid={testId}>
      <div className={`terminal-label mb-3 ${labelColor || ""}`}>{label}</div>
      {arr.length === 0 ? (
        <p className="text-xs text-muted-foreground">Not specified in the posting.</p>
      ) : (
        <ul className="space-y-2">
          {arr.map((it, i) => {
            const status = compare && profileSummary ? compareItem(it, profileSummary) : null;
            return (
              <li key={i} className="text-sm text-foreground/80 flex gap-2 items-start" data-testid={`${testId}-item-${i}`} data-compare-status={status || ""}>
                {status === "match" ? (
                  <Check className="w-3.5 h-3.5 text-accent-green mt-1 shrink-0" strokeWidth={2.5} />
                ) : status === "partial" ? (
                  <Circle className="w-3 h-3 text-accent-amber mt-1.5 shrink-0" strokeWidth={2.5} />
                ) : status === "gap" ? (
                  <AlertTriangle className="w-3.5 h-3.5 text-primary mt-1 shrink-0" strokeWidth={2} />
                ) : (
                  <span className="text-foreground/35 font-mono shrink-0">{String(i + 1).padStart(2, "0")}</span>
                )}
                <span className={status === "gap" ? "text-foreground/65" : ""}>{it}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
